import { Box, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';

import {
    clearReEntryDetails,
    setHasRecommendedTime,
    setRecurrenceDayOfWeek,
    setRecurrenceFrequency,
    setRecurrenceOccurrencePosition,
    setScheduledTimestamp,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import {
    CampaignChannels,
    EmailSendInputState,
    SendRecurrenceFrequency,
    SmsSendInputState,
    VoiceCallSendInput,
} from 'domains/campaigns/types';
import { getRecurrenceLabel } from 'domains/campaigns/utils';
import Autocomplete, { AutocompleteOption } from 'domains/core/components/Autocomplete';
import DropdownButton from 'domains/core/components/DropdownButton';
import InputLabel from 'domains/core/components/InputLabel';
import InfoSnackbar from 'domains/core/components/Snackbars/InfoSnackbar';
import theme from 'theme';

import { DisabledTooltipContent } from '../../../../components/DisabledTooltipContent';
import { disabledMessage } from '../RecommendedTimeSwitch';
import { getRecommendedTime } from '../utils/getRecommendedTime';

const WEEKDAY_MAP: Record<number, string> = { 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA', 7: 'SU' };

const OCCURRENCE_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: '1st' },
    { value: 2, label: '2nd' },
    { value: 3, label: '3rd' },
    { value: 4, label: '4th' },
    { value: -1, label: 'Last' },
];

const DAY_OF_WEEK_OPTIONS: { value: string; label: string }[] = [
    { value: 'MO', label: 'Monday' },
    { value: 'TU', label: 'Tuesday' },
    { value: 'WE', label: 'Wednesday' },
    { value: 'TH', label: 'Thursday' },
    { value: 'FR', label: 'Friday' },
    { value: 'SA', label: 'Saturday' },
    { value: 'SU', label: 'Sunday' },
];

const dropdownButtonStyles = {
    backgroundColor: theme.palette.action.active,
    textTransform: 'capitalize',
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
};

export type Props = {
    disableButton?: boolean;
    dispatchCampaignAction: React.Dispatch<any>;
    firstSend: EmailSendInputState | SmsSendInputState | VoiceCallSendInput;
    sendDate: DateTime;
    setEndDate: (payload: DateTime) => void;
    setHasEndDate: (payload: boolean) => void;
    setIsRecommendedTimeSwitchTouched: (payload: boolean) => void;
    setSendTime: (payload: DateTime) => void;
};

const RecurrenceDropdown = ({
    disableButton = false,
    dispatchCampaignAction,
    firstSend,
    sendDate,
    setEndDate,
    setHasEndDate,
    setIsRecommendedTimeSwitchTouched,
    setSendTime,
}: Props) => {
    const { monthlyRecurringSends: hasMonthlyRecurringSends } = useFlags();

    const { channel, cohortId, recurrenceFrequency, recurrenceDayOfWeek, recurrenceOccurrencePosition } = firstSend;
    const hasRecommendedTime = channel === CampaignChannels.EMAIL && firstSend.hasRecommendedTime;

    const handleDoesNotRepeatSelection = () => {
        const recurrence = SendRecurrenceFrequency.DOES_NOT_REPEAT;
        dispatchCampaignAction(setRecurrenceFrequency(recurrence));
        const newDatetime = getRecommendedTime(sendDate, recurrence);

        if (hasRecommendedTime) {
            setSendTime(newDatetime);
            dispatchCampaignAction(setScheduledTimestamp(newDatetime.toISO(), 0));
        }

        setHasEndDate(false);
        setEndDate(null);
        setIsRecommendedTimeSwitchTouched(true);
        dispatchCampaignAction(clearReEntryDetails());
    };

    const handleContinuousSelection = () => {
        dispatchCampaignAction(setRecurrenceFrequency(SendRecurrenceFrequency.CONTINUOUS));
        dispatchCampaignAction(setHasRecommendedTime(false));

        setIsRecommendedTimeSwitchTouched(true);
    };

    const handleMonthlySelection = () => {
        const weekday = sendDate.weekday;
        const dayOfWeek = WEEKDAY_MAP[weekday];
        const dayOfMonth = sendDate.day;
        const occurrencePosition =
            dayOfMonth <= 7 ? 1 : dayOfMonth <= 14 ? 2 : dayOfMonth <= 21 ? 3 : dayOfMonth <= 28 ? 4 : -1;

        dispatchCampaignAction(setRecurrenceFrequency(SendRecurrenceFrequency.MONTHLY));
        dispatchCampaignAction(setHasRecommendedTime(false));
        dispatchCampaignAction(setRecurrenceDayOfWeek(dayOfWeek));
        dispatchCampaignAction(setRecurrenceOccurrencePosition(occurrencePosition));
        setIsRecommendedTimeSwitchTouched(true);
    };

    const handleDayOfWeekChange = (_event: React.SyntheticEvent, option: AutocompleteOption<string> | null) => {
        if (option) {
            dispatchCampaignAction(setRecurrenceDayOfWeek(option.value));
        }
    };

    const handleOccurrenceChange = (_event: React.SyntheticEvent, option: AutocompleteOption<number> | null) => {
        if (option) {
            dispatchCampaignAction(setRecurrenceOccurrencePosition(option.value));
        }
    };

    const selectedOccurrenceOption = OCCURRENCE_OPTIONS.find(({ value }) => value === recurrenceOccurrencePosition);
    const selectedDayOfWeekOption = DAY_OF_WEEK_OPTIONS.find(({ value }) => value === recurrenceDayOfWeek);

    const optionsRecurrenceFrequency = [
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.DOES_NOT_REPEAT).toLowerCase(),
            onClick: handleDoesNotRepeatSelection,
        },
        ...(!cohortId
            ? [
                  {
                      label: (
                          <InputLabel
                              label="Continual"
                              labelIconTooltipText="Continual sends are delivered when the audience criteria is met."
                          />
                      ),
                      onClick: handleContinuousSelection,
                  },
              ]
            : []),
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.DAILY).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.DAILY),
        },
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.WEEKLY, sendDate?.toISO()).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.WEEKLY),
        },
        {
            label: getRecurrenceLabel(SendRecurrenceFrequency.EVERY_WEEKDAY).toLowerCase(),
            onClick: () => handleRecurrenceSelection(SendRecurrenceFrequency.EVERY_WEEKDAY),
        },
        ...(hasMonthlyRecurringSends
            ? [
                  {
                      label: getRecurrenceLabel(SendRecurrenceFrequency.MONTHLY).toLowerCase(),
                      onClick: handleMonthlySelection,
                  },
              ]
            : []),
    ];

    const handleRecurrenceSelection = (recurrence: SendRecurrenceFrequency) => {
        dispatchCampaignAction(setRecurrenceFrequency(recurrence));

        if (hasRecommendedTime) {
            const newDatetime = getRecommendedTime(sendDate, recurrence);
            setSendTime(newDatetime);
            dispatchCampaignAction(setScheduledTimestamp(newDatetime.toISO(), 0));
        }

        setIsRecommendedTimeSwitchTouched(true);
    };

    const renderRecommendedTimeSnackbar = () => {
        if (!hasRecommendedTime || recurrenceFrequency === SendRecurrenceFrequency.DOES_NOT_REPEAT) return;

        if (recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS) {
            return (
                <InfoSnackbar
                    key="continuous-recurrence-recommended-time-disabled"
                    successMessage={
                        <Typography color="info.content" component="span" variant="body2">
                            {disabledMessage}
                        </Typography>
                    }
                />
            );
        }

        return (
            <InfoSnackbar
                key={`${recurrenceFrequency}-recurrence-recommended-time-updated`}
                successMessage={
                    <Typography color="info.content" component="span" variant="body2">
                        Cured's recommended time updated based on{' '}
                        <Typography color="info.dark" component="span" variant="body2">
                            recurring send
                        </Typography>
                        .
                    </Typography>
                }
            />
        );
    };

    return (
        <>
            {renderRecommendedTimeSnackbar()}
            <Box>
                <DisabledTooltipContent disabled={disableButton}>
                    <span>
                        <DropdownButton
                            sx={dropdownButtonStyles}
                            data-testid="recurrence-button"
                            disabled={disableButton}
                            label={getRecurrenceLabel(
                                recurrenceFrequency,
                                sendDate.toISO(),
                                recurrenceDayOfWeek,
                                recurrenceOccurrencePosition
                            )}
                            options={optionsRecurrenceFrequency}
                            variant="contained"
                            size="small"
                        />
                    </span>
                </DisabledTooltipContent>
                {recurrenceFrequency === SendRecurrenceFrequency.MONTHLY && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Autocomplete
                            data-testid="occurrence-position-select"
                            disableClearable
                            fullWidth={false}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            label="Occurrence"
                            onChange={handleOccurrenceChange}
                            options={OCCURRENCE_OPTIONS}
                            textFieldSx={{ width: '9rem' }}
                            type="number"
                            value={selectedOccurrenceOption ?? null}
                        />
                        <Autocomplete
                            data-testid="day-of-week-select"
                            disableClearable
                            fullWidth={false}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            label="Day"
                            onChange={handleDayOfWeekChange}
                            options={DAY_OF_WEEK_OPTIONS}
                            textFieldSx={{ width: '10rem' }}
                            type="string"
                            value={selectedDayOfWeekOption ?? null}
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default RecurrenceDropdown;
